import dayjs from 'dayjs';
import { DATE_TIME_ISO_FORMAT } from '@aleph/shared-components';
import { getLevelByDepth, getLevelDepth } from '@aleph/supergrid';
import { getAdAccountCurrency, SpotifyLevel } from 'wb-spotify/app/routes/superGrid/utils';
import { SPOTIFY_SUPERGRID_CONFIG } from 'wb-spotify/app/routes/superGrid/config';
import {
	AdStatus,
	CampaignObjective,
	CampaignStatus,
	CreateCampaignInput,
	GroupAssetFormat,
	GroupBidStrategyInput,
	GroupBudgetType,
	GroupStatus,
} from 'wb-spotify/graphql/generated';
import {
	GROUP_PACING_OPTIONS,
} from 'wb-spotify/app/routes/superGrid/columnDefinitions/group';
import { SpotifySupergridGroupData } from 'wb-spotify/app/routes/superGrid/validation/frontendGroupSchema';
import { uniqueId } from 'lodash';

export const generateMutationHash = () => uniqueId('mutation_');

export enum SpotifyCreateLevels {
	CAMPAIGN = SpotifyLevel.CAMPAIGN,
	GROUP = SpotifyLevel.GROUP,
	AD = SpotifyLevel.AD,
}

export enum SpotifyEntityCreateStatus {
	ACTIVE = 'ACTIVE',
	// TODO: might remove, not sure if needed since we have draft now
	PAUSED = 'PAUSED',
	DRAFT = 'DRAFT',
}

type MutationData = {
	name?: string,
	currency?: string,
	parent?: any,
	hash: string
	// Comes from supergrid data for existing entities
	status?: CampaignStatus | GroupStatus | AdStatus;
};

export type SpotifyCampaignCreateData = CreateCampaignInput & MutationData;
export type SpotifyGroupCreateData = SpotifySupergridGroupData & MutationData & {
	adAccountId: string
};

export interface SpotifySuperGridMutationsConfig {
	data: {
		[SpotifyCreateLevels.CAMPAIGN]: SpotifyCampaignCreateData & { isExistingCreate: boolean }
		[SpotifyCreateLevels.GROUP]: SpotifyGroupCreateData & { isExistingCreate: boolean }
		[SpotifyCreateLevels.AD]: any & { isExistingCreate: boolean }
	}
	levelOrder: [SpotifyCreateLevels.CAMPAIGN, SpotifyCreateLevels.GROUP, SpotifyCreateLevels.AD]
}

// todo: replace with types from graphql when backend is ready
export type SpotifyCreateData = Record<string, any>;

/**
 * This is a map of dependencies between levels.
 * It is used to update values of a child entity when a specific field on parent entity is changed.
 * You can return as many fields as you want, they will be merged with the existing values.
 *
 * For changing values on the same level you should still use valueSetter
 */
export const SPOTIFY_CROSS_LEVEL_DEPENDENCIES = {
	// TODO: Check if there is any cross level dependencies for Spotify
	campaignObjective: () => ({}),
};

// name and hash are not set in default create values because they are set when the mutation is created.
// same for ad account id, its set when calling the create spotify campaign mutation
type SpotifyDefaultCampaignCreateData = Omit<SpotifyCampaignCreateData, 'campaignName' | 'campaignHash' | 'adAccountId'>;
type SpotifyDefaultGroupCreateData = Omit<SpotifyGroupCreateData, 'groupHash' | 'groupName'>;

type SpotifyDefaultCreateValues = {
	[SpotifyCreateLevels.CAMPAIGN]: (parentData: SpotifyCreateData) => SpotifyDefaultCampaignCreateData;
	[SpotifyCreateLevels.GROUP]: (parentData: SpotifyCreateData) => SpotifyDefaultGroupCreateData;
	[SpotifyCreateLevels.AD]: (parentData: SpotifyCreateData) => SpotifyCreateData;
};

export const SPOTIFY_DEFAULT_CREATE_VALUES: SpotifyDefaultCreateValues = {
	[SpotifyCreateLevels.CAMPAIGN]: () => ({
		campaignObjective: CampaignObjective.Clicks,
		isInCreation:      true,
		campaignStatus:    CampaignStatus.Active,
		// Gets overriden when mutation is created, so this is just a placeholder
		name:              'New Campaign',
		hash:              generateMutationHash(),
	}),
	// @ts-ignore: category is initially null, user needs to select it
	[SpotifyCreateLevels.GROUP]: (parentData: SpotifyCreateData) => ({
		// Gets overriden when mutation is created, so this is just a placeholder
		hash:             generateMutationHash(),
		name:             'New Group',
		category:         null,
		adAccountId:      parentData?.parent?.adAccountId,
		campaignId:       parentData?.campaignId,
		groupCategory:    '',
		groupBidStrategy: GroupBidStrategyInput.MaxBid,
		groupStatus:      GroupStatus.Active,
		groupStartTime:   dayjs().add(1, 'day').startOf('day').format(DATE_TIME_ISO_FORMAT),
		groupEndTime:     dayjs().add(1, 'day').endOf('day').format(DATE_TIME_ISO_FORMAT),
		isInCreation:     true,
		currency:         getAdAccountCurrency(parentData),
		groupBudget:      {
			type:        GroupBudgetType.Daily,
			microAmount: 1000000,
			currency:    getAdAccountCurrency(parentData),
		},
		groupAssetFormat:    GroupAssetFormat.Audio,
		groupFrequencyCaps:  [],
		groupPacing:         GROUP_PACING_OPTIONS[0].value,
		groupBidMicroAmount: 1000000,
		groupTargets:        {
			groupTargetsPodcastEpisodeTopics: [],
			groupTargetsArtists:              [],
			groupTargetsInterests:            [],
			groupTargetsPlatforms:            [],
			groupTargetsGeoTargets:           {
				countryCode:   null,
				cityIds:       [],
				dmaIds:        [],
				regionIds:     [],
				postalCodeIds: [],
			},
			groupTargetsSensitiveTopicExclusions: {
				sensitiveTopics: [],
			},
		},
	}),
	[SpotifyCreateLevels.AD]: () => ({
		adName:                                 '',
		isInCreation:                           true,
		adAdvertiserName:                       '',
		adCompanionAsset:                       null,
		adLogoAsset:                            null,
		adAsset:                                null,
		adCanvasAsset:                          null,
		adCallToActionLanguage:                 'ENGLISH',
		adCallToActionText:                     '',
		adCallToActionUrl:                      '',
		adCallToActionType:                     '',
		adTagline:                              '',
		adCallToActionKey:                      '',
		adCallToActionClickthroughUrl:          'https://',
		adStartTime:                            dayjs().add(1, 'day').startOf('day').add(1, 'second').format(DATE_TIME_ISO_FORMAT),
		adEndTime:                              dayjs().add(1, 'day').endOf('day').format(DATE_TIME_ISO_FORMAT),
		adThirdPartyTrackingUrl:                '',
		adThirdPartyTrackingMeasurementPartner: '',
	}),
};

export const getSpotifyParentLevel = (level: SpotifyLevel): SpotifyLevel => getLevelByDepth(
	SPOTIFY_SUPERGRID_CONFIG,
	getLevelDepth(SPOTIFY_SUPERGRID_CONFIG, level) - 1,
) as SpotifyLevel;
